/**
 * UpgradeModal - Modal prompting user to upgrade for premium features
 * Glass morphism design with Matrix theme
 */
import React from 'react';
import { YStack, XStack, H2, Text, Dialog, Unspaced } from 'tamagui';
import { MatrixButton } from './MatrixButton';
import { GlassCard } from './GlassCard';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  feature?: string;
}

export function UpgradeModal({ isOpen, onClose, onUpgrade, feature = 'this feature' }: UpgradeModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          animation="quick"
          opacity={0.7}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          backgroundColor="$background"
        />
        <Dialog.Content
          bordered
          elevate
          key="content"
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          backgroundColor="$background"
          borderColor="$green10"
          borderWidth={1}
          borderRadius="$4"
          padding="$5"
          maxWidth={450}
          width="90%"
        >
          <YStack gap="$4">
            {/* Header */}
            <XStack justifyContent="space-between" alignItems="center">
              <H2 color="$green10" fontFamily="$heading" fontSize="$7">
                Upgrade Required
              </H2>
              <Unspaced>
                <Dialog.Close asChild>
                  <MatrixButton
                    variant="ghost"
                    size="$2"
                    onPress={onClose}
                  >
                    &#10005;
                  </MatrixButton>
                </Dialog.Close>
              </Unspaced>
            </XStack>

            {/* Message */}
            <Text color="$color" fontSize="$4" lineHeight="$5">
              You've reached the limit for {feature} on the free tier.
              Upgrade to Pro to unlock unlimited access and premium features.
            </Text>

            {/* Feature highlights */}
            <GlassCard terminal padding="$3">
              <YStack gap="$2">
                <Text color="$green10" fontFamily="$mono" fontSize="$3" fontWeight="bold">
                  PRO TIER - $9.99/month
                </Text>
                <YStack gap="$1" paddingLeft="$2">
                  <Text color="$color" fontSize="$3">+ Unlimited ticker following</Text>
                  <Text color="$color" fontSize="$3">+ Priority earnings alerts</Text>
                  <Text color="$color" fontSize="$3">+ Advanced analytics</Text>
                  <Text color="$color" fontSize="$3">+ Email digests</Text>
                </YStack>
              </YStack>
            </GlassCard>

            {/* Actions */}
            <XStack gap="$3" justifyContent="flex-end" paddingTop="$2">
              <MatrixButton variant="ghost" onPress={onClose}>
                Maybe Later
              </MatrixButton>
              <MatrixButton variant="primary" onPress={onUpgrade}>
                View Plans
              </MatrixButton>
            </XStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
